import { pdf } from "@react-pdf/renderer";
import { Chip } from "components/Chip";
import {
  orderStatusChipVariantsMapping,
  orderStatusConstants,
  orderStatusMapping,
  orderTypeMapping,
  orderTypeConstants,
} from "constants/order";
import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { events } from "constants/tagManager";
import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import {
  getAbnSelectionProcedures,
  getNonCoveredProcedures,
} from "helpers/abn";
import { parseDate } from "helpers/date";
import { imageUrlToBase64 } from "helpers/image";
import {
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import {
  sumOfFacilityProcedures,
  sumOfProvidersProcedures,
} from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { useOrder } from "hooks/useOrder";
import usePrice from "hooks/usePrice";
import { sizer } from "layout/styles/styled/sizer";
import { Steps, OrderPayMoreSteps } from "pages/OrderOverview/utils/constants";
import { CopyOrderDialog } from "pages/Orders/components/OrdersTable/components/CopyOrderDIalog";
import { Steps as OrderUpdateSteps } from "pages/OrderUpdate/utils/constants";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import {
  useGetFinixSavedPaymentMethodQuery,
  useLazyGetFinixSavedPaymentMethodQuery,
} from "store/queries/finix";
import {
  useCreatePaymentMutation,
  useUpdateOrderMutation,
} from "store/queries/order";
import { useCreateOrderHistoryAsStaffMutation } from "store/queries/orderHistory";
import {
  useMarkOrderBlockedTransfersMutation,
  useMarkPaymentReadyForTransfersMutation,
} from "store/queries/payment";
import {
  useGetCardLastDigitsQuery,
  useLazyGetCardLastDigitsQuery,
} from "store/queries/stripe";
import { onPublicTokenChange } from "store/slices/auth";
import {
  onChangeViewOrderViewReceipt,
  onOrderOverviewStepChange,
  onOrderUpdateStepChange,
  onOrderPayMoreStepChange,
} from "store/slices/order";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { IAbnNotice } from "types/AbnNotice/AbnNotice";
import { IOrder } from "types/Order/Order";
import { OrderStatus } from "types/Order/OrderStatus";
import { IOrderHistory } from "types/OrderHistory";
import { IProvider } from "types/Provider/Provider";
import { ITermsAcceptance } from "types/TermsAcceptance/TermsAcceptance";

import { CancelOrderDialog } from "./components/CancelOrderDialog/CancelOrderDialog";
import { ClearOrderDocument } from "./components/ClearOrderDocument";
import { ClearOrderProDownloadButton } from "./components/Download";
import { DownloadAbn } from "./components/DownloadAbn/DownloadAbn";
import { MarkAsPaidCashDialog } from "./components/MarkAsPaidCashDialog/MarkAsPaidCashDialog";
import { MarkAsPaidCashUnmanagedDialog } from "./components/MarkAsPaidCashUnmanagedDialog/MarkAsPaidCashUnmanagedDialog";
import { MarkAsPaidCheckDialog } from "./components/MarkAsPaidCheckDialog/MarkAsPaidCheckDialog";
import { MoneyTransferDialog } from "./components/MoneyTransferDialog";
import { TermsAcceptancePdf } from "./components/TermsAcceptancesDialog/TermsAcceptancePdf";
import { TermsAcceptancesDialog } from "./components/TermsAcceptancesDialog/TermsAcceptancesDialog";
import { NoteToPdfDialog } from "./NoteToPdfDialog/NoteToPdfDialog";

export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

const HeadingContainer = styled.div`
  gap: ${sizer(4)};
  margin-bottom: ${sizer(2)};
`;

const StyledSubtitle = styled.h2`
  color: var(--bluegray-500);
  font-size: var(--fontsize-contents);
  font-weight: var(--font-weight-medium);

  line-height: ${sizer(5)};
`;

const Container = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: ${sizer(2)};
  justify-content: space-between;
`;

const StyledH2 = styled.h2`
  margin-bottom: ${sizer(8)};
`;

type PaymentType = "Financing" | "Cash" | "Check";

export function Header({
  order,
  onUpdateOrder,
  orderHistories,
  handleRedoOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
  orderHistories: IOrderHistory[];
  handleRedoOrder?: () => void;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { pageViewEvents } = useAnalytics();
  const { scope } = useSelector(userSelector);
  const {
    isGFE,
    mainFacility,
    isStatusSentToPatient,
    isNotReadyForTransfer,
    isStatusExpired,
    payment,
    isDefaultOrderType,
    isStatusPaid,
  } = useOrder(order);

  const { getClearPriceByOrderType, getInsuranceAmounts, insuranceAmounts } =
    usePrice();

  const { current: toastElement } = useContext(ToastContext);

  const [login] = useLoginWithOrderIdAndDOBMutation();
  const [createPayment] = useCreatePaymentMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [markPaymentReadyForTransfers] =
    useMarkPaymentReadyForTransfersMutation();
  const [markOrderBlockedTransfers] = useMarkOrderBlockedTransfersMutation();
  const { userInfo, accounts } = useUserWithAccount();

  const showPaymentPlanInfo = useMemo(() => {
    if (!order) return false;
    const astivePaymentPlan = order.paymentPlan.find((plan) => plan.active);
    return !!astivePaymentPlan?.installments?.length;
  }, [order]);

  const paymentInfoFull = useMemo(() => {
    if (!order) return {};
    const allPaidPayments =
      order?.payments.filter((aPayment) => aPayment.status === "Paid") || [];
    const firstPayment = allPaidPayments[0];
    const sortedPayments = allPaidPayments.sort((a, b) => {
      const dateA = new Date(a.createdAt || new Date(0)).getTime();
      const dateB = new Date(b.createdAt || new Date(0)).getTime();
      return dateA - dateB;
    });
    const secondPayment = sortedPayments[1];
    return {
      firstPayment,
      secondPayment,
    };
  }, [order]);

  const paymentPlanInfo = useMemo(() => {
    if (!order) return null;
    const activePaymentPlan = [...order.paymentPlan].find(
      (plan) => plan.active
    );

    return {
      ...activePaymentPlan,
    };
  }, [order]);

  const stripeMethodId = useMemo<string | null>(() => {
    if (!showPaymentPlanInfo) {
      return paymentInfoFull?.firstPayment?.stripePaymentMethodId || null;
    }
    return paymentPlanInfo?.stripePaymentMethodId || null;
  }, [showPaymentPlanInfo, paymentInfoFull, paymentPlanInfo]);

  const account = useMemo(() => {
    if (!accounts) return null;
    const account = accounts.data.find((acc) => acc.id === order.accountId);
    return account;
  }, [accounts, order.accountId]);

  const [showMarkPaidViaCashDialog, setShowMarkPaidViaCashDialog] =
    useState(false);
  const [
    showMarkPaidViaCashUnmanagedDialog,
    setShowMarkPaidViaCashUnmanagedDialog,
  ] = useState(false);
  const [showMarkPaidViaCheckDialog, setShowMarkPaidViaCheckDialog] =
    useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelNotes, setCancelNotes] = useState("");
  const [transfers, setTransfers] = useState([
    {
      selectedPartyToTransferFrom: null,
      selectedPartyToTransferTo: null,
      selectedAmountToTransfer: 0,
    },
  ]);
  const [showMoneyTransferDialog, setShowMoneyTransferDialog] = useState(false);
  const [showCopyOrderDialog, setShowCopyOrderDialog] = useState(false);
  const [providerToCharge, setProviderToCharge] = useState<string>();
  const [isAuthorizationDone, setIsAuthorizationDone] = useState(false);

  // Strict-freeze: this order's rail is whatever it is BOUND to (authoritative
  // endpoint via the hook), not the account's current useFinix flag. Keeps the
  // cancel dialog, saved-card display and money-transfer UI on the correct
  // processor for a Stripe-era order whose account was later flipped to Finix.
  const {
    isFinix: useFinix,
    isLoading: isProviderLoading,
    isError: isProviderError,
  } = useFinixPaymentProvider(order);
  // Strict-freeze error-fallback: the rail-specific admin controls below are
  // hidden until the rail is definitively known (not loading and not errored),
  // rather than shown on a guess from the account flag.
  const isRailUnknown = isProviderLoading || isProviderError;

  const {
    data: cardData,
    isFetching: isFetchingCard,
    isLoading: isLoadingCard,
  } = useGetCardLastDigitsQuery(stripeMethodId, {
    skip:
      useFinix || !stripeMethodId || !showCancelDialog || !isAuthorizationDone,
  });
  const {
    data: finixCardData,
    isFetching: isFetchingFinixCard,
    isLoading: isLoadingFinixCard,
  } = useGetFinixSavedPaymentMethodQuery(order?.id, {
    skip: !useFinix || !order?.id || !showCancelDialog || !isAuthorizationDone,
  });
  const cardLastDigits = useFinix
    ? finixCardData?.data?.cardDetails?.last4
    : cardData?.data?.last4;
  const isCardQueryLoading = useFinix
    ? isLoadingFinixCard || isFetchingFinixCard
    : isLoadingCard || isFetchingCard;
  const showPartialPayment = useMemo(() => {
    // show only for those orders that are paid and do not have any cash payment
    return (
      [orderStatusConstants.PAID, orderStatusConstants.PARTIALLY_PAID].includes(
        order.status
      ) && !order.payments.some((payment) => payment.type === "Cash")
    );
  }, [order]);

  const showReceiptButton = useMemo(() => {
    const orderPayments = order.payments;
    return (
      [
        orderStatusConstants.PAID,
        orderStatusConstants.PARTIALLY_PAID,
        orderStatusConstants.CANCELED,
        orderStatusConstants.REFUNDED,
      ].includes(order.status) && !!orderPayments.length
    );
  }, [order]);

  const payMoreButtonDisabled = useMemo(() => {
    if (
      (order.status === orderStatusConstants.PARTIALLY_PAID ||
        order.status === orderStatusConstants.PAID) &&
      order?.paymentPlanBreakdown?.payments?.length
    ) {
      const iniitalDeposit = order.paymentPlanBreakdown.payments[0];
      const paymentsWithoutInitialDeposit =
        order.paymentPlanBreakdown.payments.filter(
          (p: any) => !!p.installmentInDb
        );
      const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
        (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
        null
      );
      if (lastPaidInstallment) {
        return !lastPaidInstallment.transferredToProviders;
      }
      return !iniitalDeposit.transferredToProviders;
    }
    return false;
  }, [order]);

  // only show the pay remaining amount button for partially paid orders
  const showPayRemainingAmountButton = useMemo(() => {
    return [orderStatusConstants.PARTIALLY_PAID].includes(order.status);
  }, [order]);

  useEffect(() => {
    getInsuranceAmounts(order.gfeQuote?.pricingBreakdown);
  }, [order.gfeQuote?.pricingBreakdown]);

  // Reset authorization when dialog closes
  useEffect(() => {
    if (!showCancelDialog) {
      setIsAuthorizationDone(false);
    }
  }, [showCancelDialog]);

  const { clearPrice, percentageOff } = getClearPriceByOrderType({
    order,
  });

  const gfeNoDiscountPdf = useMemo(() => {
    const gfeCustoms = order.account.customizes.find(
      (c) => c.orderType === orderTypeConstants.GFE
    );
    return gfeCustoms?.downloadPdfContent?.noDiscountPdf;
  }, [order]);

  const gfeAddNoteToPdf = useMemo(() => {
    const gfeCustoms = order.account.customizes.find(
      (c) => c.orderType === orderTypeConstants.GFE
    );
    return gfeCustoms?.downloadPdfContent?.addNoteToPdf;
  }, [order]);

  const showDownloadWithNote = useMemo(() => {
    if (isGFE && gfeAddNoteToPdf) return true;
    return false;
  }, [isGFE, gfeNoDiscountPdf, gfeAddNoteToPdf]);

  const isVersion3Pdf = useMemo(() => {
    const { orderType } = order;
    const customs = order.account.customizes.find(
      (c) => c.orderType === orderType
    );
    return !!customs?.downloadPdfContent?.version3Pdf;
  }, [order]);

  const gfeDisclaimer = useMemo(() => {
    const gfeCustoms = order.account.customizes.find(
      (c) => c.orderType === orderTypeConstants.GFE
    );
    const disclaimerEnglish = {
      title: gfeCustoms?.overviewPage.disclaimerTitleEnglish,
      description: gfeCustoms?.overviewPage.disclaimerDescriptionEnglish,
    };
    const disclaimerSpanish = {
      title: gfeCustoms?.overviewPage.disclaimerTitleSpanish,
      description: gfeCustoms?.overviewPage.disclaimerDescriptionSpanish,
    };
    return {
      disclaimerEnglish,
      disclaimerSpanish,
    };
  }, [order]);

  const getAmountFinancingOrCash = (paymentType: PaymentType) => {
    let amountInCents: number;

    if (paymentType === "Financing") {
      amountInCents = order.amountInCents;
    } else {
      amountInCents = clearPrice;
    }

    return amountInCents;
  };

  const markViaFinancingOrCashOrCheck = (
    paymentType: PaymentType,
    notes?: string
  ) => {
    const amountInCents: number = getAmountFinancingOrCash(paymentType);

    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));

        createPayment({
          orderId: order.id,
          amountInCents,
          paymentType,
          providerId: providerToCharge,
          userId: userInfo?.id,
          note: notes?.trim()?.length ? notes.trim() : undefined,
        })
          .unwrap()
          .then(async () => {
            onUpdateOrder();
            pageViewEvents(
              {
                accountName: order.account.name,
                orderType: order.orderType,
                communicationMethod: order.patient.preferredContactMethod,
              },
              events.CLICK_FINANCING
            );

            toastElement?.show({
              summary: "Success!",
              severity: "success",
              detail: "Your estimate has been updated.",
            });
          })
          .catch(() => {
            toastElement?.show({
              severity: "error",
              detail: "Try again later.",
              summary: "Something went wrong.",
            });
          });
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const acceptPayment = (step: Steps) => {
    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));
        dispatch(
          onOrderOverviewStepChange({
            step,
          })
        );
        pageViewEvents(
          {
            accountName: order.account.name,
            orderType: order.orderType,
            communicationMethod: order.patient.preferredContactMethod,
          },
          events.CLICK_ACCEPT_PAYMENT
        );
        navigate(`/order/${order.externalId}?internalUser=true`);
      })
      .catch(() => {
        const detail =
          "Authentication failed. Please check your date of birth.";

        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const payRemainingAmount = (step: OrderPayMoreSteps) => {
    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));
        dispatch(
          onOrderPayMoreStepChange({
            step,
          })
        );
        pageViewEvents(
          {
            accountName: order.account.name,
            orderType: order.orderType,
            communicationMethod: order.patient.preferredContactMethod,
          },
          events.CLICK_ACCEPT_PAYMENT
        );
        navigate(`/order/pay/${order.externalId}?internalUser=true`);
      })
      .catch(() => {
        const detail =
          "Authentication failed. Please check your date of birth.";

        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const handleShowReceipt = () => {
    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));
        dispatch(onChangeViewOrderViewReceipt(true));
      })
      .catch(() => {
        const detail = "Failed to show receipt";

        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const changeCardDetails = (step: OrderUpdateSteps) => {
    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));
        dispatch(
          onOrderUpdateStepChange({
            step,
          })
        );
        pageViewEvents(
          {
            accountName: order.account.name,
            orderType: order.orderType,
            communicationMethod: order.patient.preferredContactMethod,
          },
          events.CLICK_ACCEPT_PAYMENT
        );
        navigate(`/order/update/${order.externalId}?internalUser=true`);
      })
      .catch(() => {
        const detail =
          "Authentication failed. Please check your date of birth.";

        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const changeOrderStatus = (newStatus: OrderStatus) => {
    updateOrder({
      id: order.id,
      status: newStatus,
    })
      .then(() => {
        onUpdateOrder();
        toastElement?.show({
          severity: "success",
          summary: "Estimate status changed",
          detail: "Estimate status changed successfully",
        });
      })
      .catch((error) => {
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: `Message: ${error.message}`,
        });
      });
  };

  const handleOnClickToMarkCashPaid = () => {
    // Route to the correct dialog based on which feature flag is enabled
    // CashUnmanaged takes priority if enabled
    if (account?.features.markPaidViaCashUnmanaged) {
      setShowMarkPaidViaCashUnmanagedDialog(true);
    } else {
      setShowMarkPaidViaCashDialog(true);
    }
  };

  const handleOnClickToMarkCheckPaid = () => {
    setShowMarkPaidViaCheckDialog(true);
  };

  const handleOnClickToMarkReadyForTransfer = () => {
    confirmDialog({
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      message: `Are you sure you want to mark the payment on this estimate as ready for Stripe transfers?`,
      accept: () => {
        if (!payment?.id) return;
        markPaymentReadyForTransfers(payment.id)
          .unwrap()
          .then(() => {
            onUpdateOrder();
            toastElement?.show({
              severity: "success",
              summary: "Ready for transfers",
              detail: "Stripe transfers will be initiated shortly",
            });
          })
          .catch((error) =>
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail: `Message: ${error}`,
            })
          );
      },
    });
  };

  const handleOnClickToMarkBlockedOrderForTransfer = () => {
    confirmDialog({
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      message: `Are you sure you want to block transfer(s) for this estimate?`,
      accept: () => {
        if (!order?.id) return;
        markOrderBlockedTransfers(order.id)
          .unwrap()
          .then(() => {
            onUpdateOrder();
            toastElement?.show({
              severity: "success",
              summary: "Blocked for transfers",
              detail: "Transfers are blocked for the estimate",
            });
          })
          .catch((error) =>
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail: `Message: ${error}`,
            })
          );
      },
    });
  };

  const handleOnClickToCancelOrder = () => {
    setShowCancelDialog(true);
    login({
      orderId: order.externalId || "",
      dateOfBirth: new Date(order.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: order.id }));
        setIsAuthorizationDone(true);
      })
      .catch(() => {
        const detail = "Failed to authenticate. Please try again.";

        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const handleOnClickPartialRefund = () => {
    setShowMoneyTransferDialog(true);
  };

  const handleOnClickToChangeOrderStatus = (value: OrderStatus) => {
    confirmDialog({
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      message: `Are you sure you want to change this estimate status?`,
      accept: () => changeOrderStatus(value),
    });
  };

  const handleGoToOrdersPage = () => {
    navigate(`/hospitals/view/${order.account.id}${location.search}`);
  };

  const [showNoteToPdfDialog, setShowNoteToPdfDialog] = useState(false);
  const [noteForPdfDialog, setNoteForPdfDialog] = useState("");
  const [hoursEstimated, setHoursEstimated] = useState<number | null>(null);
  const [showTermsAcceptancesDialog, setShowTermsAcceptancesDialog] =
    useState(false);
  const [createOrderHistory] = useCreateOrderHistoryAsStaffMutation();

  const termsAcceptances = order.termsAcceptances ?? [];

  // Pre-fetch logo as base64 to ensure it's loaded before PDF generation
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [isLogoLoading, setIsLogoLoading] = useState(true);

  const [triggerStripeCardLookup] = useLazyGetCardLastDigitsQuery();
  const [triggerFinixCardLookup] = useLazyGetFinixSavedPaymentMethodQuery();

  const fetchCardInfoForTermsPdf = async (): Promise<
    { brand: string; last4: string } | undefined
  > => {
    try {
      if (useFinix) {
        const result = await triggerFinixCardLookup(order.id).unwrap();
        const cd = result?.data?.cardDetails;
        if (cd?.brand && cd?.last4) {
          return { brand: cd.brand, last4: cd.last4 };
        }
        return undefined;
      }

      const activePaymentPlan = order.paymentPlan?.find((p) => p.active);
      const hasInstallments = !!activePaymentPlan?.installments?.length;
      const firstPaidPayment = order.payments?.find((p) => p.status === "Paid");
      const methodId = hasInstallments
        ? activePaymentPlan?.stripePaymentMethodId
        : firstPaidPayment?.stripePaymentMethodId;
      if (!methodId) return undefined;

      const result: any = await triggerStripeCardLookup(methodId).unwrap();
      const cd = result?.data;
      if (cd?.brand && cd?.last4) {
        return { brand: cd.brand, last4: cd.last4 };
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  const downloadTermsAcceptancePdf = async (
    termsAcceptance: ITermsAcceptance
  ) => {
    try {
      const cardInfo = await fetchCardInfoForTermsPdf();
      const blob = await pdf(
        <TermsAcceptancePdf
          termsAcceptance={termsAcceptance}
          order={order}
          orderHistories={orderHistories}
          patientFirstName={order.patient.firstName}
          patientLastName={order.patient.lastName}
          patientDateOfBirth={order.patient.dateOfBirth}
          facilityLogo={logoBase64 ?? order.account.accountLogoUrl ?? ""}
          fullAddress={
            mainFacility
              ? `${mainFacility.streetAddress}, ${mainFacility.city}, ${mainFacility.state}`
              : undefined
          }
          phoneNumber={mainFacility?.publicContactPhone}
          facilityName={mainFacility?.name}
          cardInfo={cardInfo}
        />
      ).toBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `terms_acceptance_${termsAcceptance.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Terms acceptance PDF generation failed:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate terms acceptance PDF.",
      });
    }
  };

  const handleOnClickTermsAcceptances = () => {
    if (termsAcceptances.length === 1) {
      downloadTermsAcceptancePdf(termsAcceptances[0]);
      return;
    }
    setShowTermsAcceptancesDialog(true);
  };
  useEffect(() => {
    const logoUrl = order.account.accountLogoUrl;
    if (!logoUrl) {
      setLogoBase64("");
      setIsLogoLoading(false);
      return;
    }

    setIsLogoLoading(true);
    imageUrlToBase64(logoUrl)
      .then((base64) => {
        setLogoBase64(base64);
      })
      .finally(() => {
        setIsLogoLoading(false);
      });
  }, [order.account.accountLogoUrl]);

  const sortedOrderHistories = useMemo(() => {
    return [...orderHistories].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [orderHistories]);
  const mostRecentNote = useMemo(() => {
    // Ensure the array is sorted in descending order by createdAt
    const sortedNotes = sortedOrderHistories.filter(
      (item) => item.type === orderHsitoryTypeConstants.NoteToPdf
    );

    // Access the first item of the sorted array
    return sortedNotes.length ? sortedNotes[0].note || "" : "";
  }, [sortedOrderHistories]);

  const showRedo = useMemo(() => {
    if (
      [
        orderStatusConstants.SENT_TO_PATIENT,
        orderStatusConstants.NO_BALANCE_DUE,
        orderStatusConstants.EXPIRED,
      ].includes(order.status)
    ) {
      return true;
    }
    if (
      order.status === orderStatusConstants.CANCELED &&
      !order.payments.length
    ) {
      return true;
    }
    return false;
  }, [order]);

  const handleOnClickToAddNoteForPdf = () => {
    setNoteForPdfDialog(mostRecentNote);
    setShowNoteToPdfDialog(true);
  };

  const breakdown = useMemo(() => {
    return order?.paymentFullBreakdown?.bundledProviderAmounts;
  }, [order]);

  const insuranceBenefits = useMemo(() => {
    if (!order || order.orderType !== orderTypeConstants.GFE) return null;
    // CLE-2635 — resolve through the eligibility archive when there is no quote.
    const benefits = resolveOrderBenefits(order);
    const secondary = resolveOrderSecondaryBenefits(order);
    const primaryInsuranceDetails = {
      name: order?.payer?.name,
      deductible: benefits.remainingDeductibleInCents,
      outOfPocketMax: benefits.outOfPocketMaxInCents,
      flatCopay: benefits.flatCopayInCents,
      coinsurancePercent: benefits.coinsurancePercent,
      benefitsUpdatedAt: benefits.benefitsUpdatedAt,
    };
    const secondaryInsuranceDetails = order?.secondaryPayer
      ? {
          name: order?.secondaryPayer?.name,
          deductible: secondary.remainingDeductibleInCents,
          outOfPocketMax: secondary.outOfPocketMaxInCents,
          flatCopay: secondary.flatCopayInCents,
          coinsurancePercent: secondary.coinsurancePercent,
          benefitsUpdatedAt: secondary.benefitsUpdatedAt,
        }
      : null;
    return [primaryInsuranceDetails, secondaryInsuranceDetails].filter(
      (insurance) => insurance !== null
    );
  }, [order]);

  const nonCoveredProcedures = useMemo(() => {
    return getNonCoveredProcedures(order);
  }, [order]);

  const paymentBreakdown = useMemo(() => {
    if (!order || order.orderType !== orderTypeConstants.GFE) return null;
    const nonCoveredCPTCodes = nonCoveredProcedures.map((p: any) => p?.code);
    const coveredProcedures =
      order?.paymentFullBreakdownByCPTCode?.bundledProviderAmounts?.filter(
        (p: any) => !nonCoveredCPTCodes.includes(p?.cptCode)
      );

    const grouped: Record<
      string,
      {
        specialty: string;
        insurance_rate: number;
        insurance_pays: number;
        you_pay: number;
      }
    > = {};

    coveredProcedures?.forEach((item: any) => {
      const key = item?.specialty || "UNKNOWN";
      const amountInCents = item?.adjustedAllowablePrice || 0;
      const owedByPatientTotal = item?.patientPortion || 0;

      if (!grouped[key]) {
        grouped[key] = {
          specialty: key,
          insurance_rate: 0,
          insurance_pays: 0,
          you_pay: 0,
        };
      }

      grouped[key].insurance_rate += amountInCents;
      grouped[key].you_pay += owedByPatientTotal;
    });

    return Object.values(grouped).map((entry) => ({
      ...entry,
      insurance_pays: entry.insurance_rate - entry.you_pay,
    }));
  }, [order, nonCoveredProcedures]);

  const abnSelectionProcedures = useMemo(() => {
    if (!isGFE) return null;
    return getAbnSelectionProcedures(order);
  }, [order, isGFE]);

  // find the active ABN notice for the current payer and current procedures that require ABN
  const activeAbnNotice = useMemo(() => {
    if (!order || !isGFE) return null;
    const currentPayer = order?.payer;
    const abnNotice = order?.abnNotices?.find(
      (notice) => !notice.isVoided && notice.payerId === currentPayer?.id
    );
    if (!abnNotice) return null;
    // If lengths differ, procedures have changed
    if (
      abnSelectionProcedures?.length !==
      abnNotice?.documentSnapshot?.procedures.length
    ) {
      return null;
    }
    // Create a map of current signed procedures
    const initialProceduresMap = new Map(
      abnNotice?.documentSnapshot?.procedures.map((proc: any) => [
        proc.cptCode,
        { description: proc.cptDescription, amount: proc.amount },
      ])
    ) as any;
    // Check that every current procedure exists in the initial set with identical values
    const allMatch = abnSelectionProcedures?.every((currentProc: any) => {
      const initialProc = initialProceduresMap.get(currentProc.cptCode);
      return (
        initialProc &&
        initialProc.description === currentProc.cptDescription &&
        initialProc.amount === currentProc.amount
      );
    });
    return allMatch ? (abnNotice as unknown as IAbnNotice) : null;
  }, [order, abnSelectionProcedures]);

  // at the moment, this is made for orders where only one provider can have a custom PDF, so we just need to find the first one
  const providerWithCustomPdf = useMemo(() => {
    return order?.providerProcedures?.find(
      (providerProcedure) => providerProcedure?.provider?.customPdfContent
    )?.provider as IProvider;
  }, [order.providerProcedures]);

  // by default our disclaimer has hours estimated, so if for the provider disclaimer is enabled, we show hours estimated field
  const providerWithCustomPdfContentHasDisclaimer = useMemo(() => {
    return !!providerWithCustomPdf?.customPdfContent?.showDisclaimer;
  }, [providerWithCustomPdf]);

  // Define the data object for PDF download outside the function
  // Use pre-fetched base64 logo to ensure image is loaded before PDF generation
  const dataForPdfDownload = {
    facilityLogo: logoBase64 ?? order.account.accountLogoUrl ?? "",
    name: `${order.patient.firstName} ${order.patient.lastName}`,
    fullAddress: `${mainFacility.streetAddress}, ${mainFacility.city}, ${mainFacility.state}`,
    phoneNumber: mainFacility.publicContactPhone,
    dateOfBirth: parseDate(order.patient.dateOfBirth),
    payerName: (order.serviceDetails as any)?.payerName,
    gfeNoDiscountPdf,
    gfeDisclaimer,
    serviceDate: order.dateOfService,
    serviceDetails: order.serviceDetails,
    facilityName: mainFacility.name,
    gfeQuote: order.gfeQuote,
    clearDiscount: percentageOff,
    clearPriceToPay: clearPrice,
    facilityFee: sumOfFacilityProcedures(order.serviceDetails.procedures),
    accountNumber: order.accountNumber,
    providerFee: sumOfProvidersProcedures(order.serviceDetails.procedures),
    providerWithCustomPdf,
    isGFE,
    insuranceAmounts,
    isVersion3Pdf,
    hoursEstimated,
    fullProviderBreakdown: breakdown,
    paymentFullBreakdownByCPTCode: order?.paymentFullBreakdownByCPTCode || [],
    clearPatientFeeValueInPercentage: order?.clearPatientFeeValueInPercentage
      ? Number(order?.clearPatientFeeValueInPercentage)
      : null,
    // CLE-2798: the statement under the table prints only for an estimate whose payer has the surcharge.
    gfeCreditCardFeeValueInPercentage: order?.gfeCreditCardFeeValueInPercentage
      ? Number(order.gfeCreditCardFeeValueInPercentage)
      : null,
    orderType: order?.orderType,
    insuranceBenefits,
    paymentBreakdown,
    activeAbnNotice,
  };

  const cancelOrderRefundAmount = useMemo(() => {
    return order?.payments?.reduce(
      (acc, payment) => (acc + (payment?.amountInCents || 0)) as number,
      0
    );
  }, [order?.payments]);

  const handleSaveNoteForPdf = async (note: string) => {
    try {
      // Generate and download the PDF after successful order history creation
      const blob = await pdf(
        <ClearOrderDocument {...dataForPdfDownload} note={note} />
      ).toBlob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `clear_order__${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      // Check if the note has changed
      if (note.trim() !== mostRecentNote.trim()) {
        // Create order history only if the note has changed
        await createOrderHistory({
          orderId: order.id,
          type: orderHsitoryTypeConstants.NoteToPdf,
          note: note.trim(),
          userId: userInfo?.id,
        }).unwrap();

        onUpdateOrder();
        toastElement?.show({
          severity: "success",
          summary: "Success!",
          detail: "Your note has been added successfully.",
        });

        link.click();
        document.body.removeChild(link);
      } else {
        // If the note hasn't changed, download the PDF immediately
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("PDF generation/download failed:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate PDF. Please try again or contact support.",
      });
    }
  };

  return (
    <Container>
      <MarkAsPaidCashDialog
        paymentType="Cash"
        markViaFinancingOrCashOrCheck={markViaFinancingOrCashOrCheck}
        order={order}
        showDialog={showMarkPaidViaCashDialog}
        setShowDialog={setShowMarkPaidViaCashDialog}
        providerToCharge={providerToCharge}
        setProviderToCharge={setProviderToCharge}
      />
      <MarkAsPaidCashUnmanagedDialog
        order={order}
        showDialog={showMarkPaidViaCashUnmanagedDialog}
        setShowDialog={setShowMarkPaidViaCashUnmanagedDialog}
        onUpdateOrder={onUpdateOrder}
      />
      <MarkAsPaidCheckDialog
        markViaFinancingOrCashOrCheck={markViaFinancingOrCashOrCheck}
        showDialog={showMarkPaidViaCheckDialog}
        setShowDialog={setShowMarkPaidViaCheckDialog}
      />
      <MoneyTransferDialog
        showDialog={showMoneyTransferDialog}
        order={order}
        setShowDialog={setShowMoneyTransferDialog}
        transfers={transfers}
        setTransfers={setTransfers}
        onUpdateOrder={onUpdateOrder}
      />
      <CancelOrderDialog
        showCancelDialog={showCancelDialog}
        setShowCancelDialog={setShowCancelDialog}
        cancelNotes={cancelNotes}
        setCancelNotes={setCancelNotes}
        orderId={order.id}
        onUpdateOrder={onUpdateOrder}
        cancelOrderRefundAmount={cancelOrderRefundAmount}
        cardLastDigits={cardLastDigits}
        isLoading={isCardQueryLoading || !isAuthorizationDone}
        setIsAuthorizationDone={setIsAuthorizationDone}
      />
      <div>
        <StyledButton
          data-testid="orders-dashboard_OrderHeader"
          icon="pi pi-arrow-left"
          onClick={handleGoToOrdersPage}
          className="p-button-text p-button-secondary p-0"
        >
          <p className="small-text">Estimates Dashboard</p>
        </StyledButton>
        <StyledH2>{order?.account.name}</StyledH2>

        <HeadingContainer className="flex align-items-center">
          <h1>Estimate Details</h1>
          {isStatusExpired ? (
            <Dropdown
              data-testid="change-order-status-dropdown_OrderHeader"
              className="p-dropdown-chip"
              value={orderStatusMapping[order?.status]}
              onChange={(e) => {
                handleOnClickToChangeOrderStatus(e.value.code as OrderStatus);
              }}
              options={[
                {
                  name: "Change status to Sent",
                  code: orderStatusConstants.SENT_TO_PATIENT,
                },
              ]}
              optionLabel="name"
              placeholder="Expired"
            />
          ) : (
            <Chip variant={orderStatusChipVariantsMapping[order?.status]}>
              {orderStatusMapping[order?.status]}
            </Chip>
          )}
        </HeadingContainer>

        <StyledSubtitle>
          {`${orderTypeMapping[order?.orderType]} • Created on ${parseDate(
            order?.createdAt || "",
            false,
            true
          )}`}
        </StyledSubtitle>
      </div>

      <div className="flex gap-3 flex-wrap justify-end">
        <Tooltip
          target=".cancel-order-button"
          style={{
            fontSize: "13px",
            width: "200px",
          }}
        />
        {order.status !== orderStatusConstants.CANCELED && (
          <span
            data-pr-tooltip="Last transfer is still being processed"
            className={payMoreButtonDisabled ? "cancel-order-button" : ""}
          >
            <StyledButton
              data-testid="cancel-order_OrderHeader"
              icon="pi pi-times"
              className="p-button-text p-button-primary p-button-danger p-0 mb-0"
              onClick={handleOnClickToCancelOrder}
            >
              <p className="small-text">
                {[
                  orderStatusConstants.PAID,
                  orderStatusConstants.PARTIALLY_PAID,
                ].includes(order.status)
                  ? "Cancel and refund"
                  : "Cancel Estimate"}
              </p>
            </StyledButton>
          </span>
        )}
        {showPayRemainingAmountButton && (
          <StyledButton
            data-testid="accept-remaining-amount-payment_OrderHeader"
            icon="pi pi-check-circle"
            className="p-button-text p-button-primary p-0 mb-0"
            onClick={() => payRemainingAmount(OrderPayMoreSteps.PAY_MORE)}
            disabled={payMoreButtonDisabled}
          >
            <p className="small-text">Pay Additional Amount</p>
          </StyledButton>
        )}
        {showReceiptButton && (
          <StyledButton
            data-testid="payment-receipt_OrderHeader"
            icon="pi pi-file"
            className="p-button-text p-button-primary p-0 mb-0 "
            onClick={handleShowReceipt}
          >
            <p className="small-text">Receipt</p>
          </StyledButton>
        )}
        {isStatusSentToPatient && (
          <>
            {(account?.features.markPaidViaCash ||
              account?.features.markPaidViaCashUnmanaged) && (
              <StyledButton
                data-testid="mark-paid-cash_OrderHeader"
                icon="pi pi-check-circle"
                className="p-button-text p-button-primary p-0 mb-0"
                onClick={() => handleOnClickToMarkCashPaid()}
              >
                <p className="small-text">Mark paid via cash</p>
              </StyledButton>
            )}
            {account?.features.markPaidViaCheck && (
              <StyledButton
                data-testid="mark-paid-cash_OrderHeader"
                icon="pi pi-check-circle"
                className="p-button-text p-button-primary p-0 mb-0"
                onClick={() => handleOnClickToMarkCheckPaid()}
              >
                <p className="small-text">Mark paid via check</p>
              </StyledButton>
            )}
            <StyledButton
              data-testid="accept-payment_OrderHeader"
              icon="pi pi-check-circle"
              className="p-button-text p-button-primary p-0 mb-0"
              onClick={() => acceptPayment(Steps.ORDER_OVERVIEW)}
            >
              <p className="small-text">Accept payment</p>
            </StyledButton>
          </>
        )}
        {isNotReadyForTransfer &&
          scope === UserRole.SUPER_ADMIN &&
          !useFinix &&
          !isRailUnknown && (
            <StyledButton
              data-testid="ready-for-transfer_OrderHeader"
              icon="pi pi-check-circle"
              className="p-button-text p-button-primary p-0 mb-0"
              onClick={() => handleOnClickToMarkReadyForTransfer()}
            >
              <p className="small-text">Ready for Transfer</p>
            </StyledButton>
          )}
        {!isNotReadyForTransfer &&
          isStatusPaid &&
          scope === UserRole.SUPER_ADMIN &&
          !useFinix &&
          !isRailUnknown && (
            <StyledButton
              data-testid="ready-for-transfer_OrderHeader"
              icon="pi pi-check-circle"
              className="p-button-text p-button-primary p-0 mb-0"
              onClick={() => handleOnClickToMarkBlockedOrderForTransfer()}
            >
              <p className="small-text">Block Stripe Transfer</p>
            </StyledButton>
          )}

        {showPartialPayment && (
          <StyledButton
            data-testid="money-transfer_OrderHeader"
            icon="pi pi-file"
            className="p-button-text p-button-primary p-0 mb-0"
            onClick={handleOnClickPartialRefund}
          >
            <p className="small-text">Partial Refund</p>
          </StyledButton>
        )}
        {order.status === orderStatusConstants.PARTIALLY_PAID && (
          <StyledButton
            data-testid="update-card_OrderHeader"
            icon="pi pi-credit-card"
            className="p-button-text p-button-primary p-0 mb-0"
            onClick={() => {
              changeCardDetails(OrderUpdateSteps.ORDER_UPDATE);
            }}
          >
            <p className="small-text">Update Card</p>
          </StyledButton>
        )}
        <NoteToPdfDialog
          showDialog={showNoteToPdfDialog}
          setShowDialog={setShowNoteToPdfDialog}
          onSaveNote={handleSaveNoteForPdf}
          note={noteForPdfDialog}
          setNote={setNoteForPdfDialog}
          showHoursEstimated={
            isVersion3Pdf &&
            (!providerWithCustomPdf ||
              providerWithCustomPdfContentHasDisclaimer)
          }
          hoursEstimated={hoursEstimated}
          setHoursEstimated={setHoursEstimated}
        />

        {(isGFE || isDefaultOrderType) &&
          (showDownloadWithNote || isVersion3Pdf ? (
            <StyledButton
              data-testid="add-note-to-pdf_OrderHeader"
              icon={isLogoLoading ? "pi pi-spin pi-spinner" : "pi pi-file"}
              className="p-button-text p-button-primary p-0 mb-0"
              onClick={handleOnClickToAddNoteForPdf}
              disabled={isLogoLoading}
            >
              <p className="small-text">
                {isLogoLoading ? "Loading..." : "Download PDF"}
              </p>
            </StyledButton>
          ) : (
            <ClearOrderProDownloadButton
              data={dataForPdfDownload}
              isLogoLoading={isLogoLoading}
            />
          ))}
        {activeAbnNotice && (
          <DownloadAbn
            data={dataForPdfDownload}
            isLogoLoading={isLogoLoading}
          />
        )}

        {termsAcceptances.length > 0 && (
          <StyledButton
            data-testid="terms-acceptances_OrderHeader"
            icon="pi pi-file"
            className="p-button-text p-button-primary p-0 mb-0"
            onClick={handleOnClickTermsAcceptances}
          >
            <p className="small-text">Terms PDF</p>
          </StyledButton>
        )}

        <TermsAcceptancesDialog
          visible={showTermsAcceptancesDialog}
          onClose={() => setShowTermsAcceptancesDialog(false)}
          termsAcceptances={termsAcceptances}
          onDownload={downloadTermsAcceptancePdf}
        />

        {showRedo ? (
          <StyledButton
            data-testid="redo-order_OrderHeader"
            icon="pi pi-undo"
            className="p-button-text p-button-primary p-0 mb-0"
            onClick={handleRedoOrder}
          >
            <p className="small-text">Redo Estimate</p>
          </StyledButton>
        ) : null}
        <StyledButton
          data-testid="duplicate-order_OrderHeader"
          icon="pi pi-clone"
          className="p-button-text p-button-primary p-0 mb-0"
          onClick={() => setShowCopyOrderDialog(true)}
        >
          <p className="small-text">Duplicate Estimate</p>
        </StyledButton>
      </div>
      <CopyOrderDialog
        showCopyOrderDialog={showCopyOrderDialog}
        setShowCopyOrderDialog={setShowCopyOrderDialog}
        setOrderToCopy={() => {}}
        orderId={order.id}
        accountId={order.accountId}
      />
    </Container>
  );
}
